import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getMenu } from '../../../../services/Admin/SalesScreenApi';
import { getAiDrinks } from '../../../../services/Admin/AiDrinksApi';
import { resolveImageUrl } from '../../../../services/indexMediaBaseUrl';

const getVisibleProducts = (products) =>
  Array.isArray(products)
    ? products.filter((product) => Number(product?.status) !== 0)
    : [];

const getMenuCategories = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.categories)) return data.categories;
  return [];
};

const getMenuDrinks = (data) => {
  if (Array.isArray(data?.drinks)) return data.drinks;
  return [];
};

const isPublicApprovedDrink = (drink) =>
  Number(drink?.is_approved) === 1 &&
  Number(drink?.is_public) === 1 &&
  Number(drink?.status) === 1;

const MenuPage = () => {
  const [categories, setCategories] = useState([]);
  const [aiDrinks, setAiDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [menuResponse, aiDrinksResponse] = await Promise.all([
        getMenu(),
        getAiDrinks()
      ]);
      
      const menuData = menuResponse?.data;
      const menuDrinks = getMenuDrinks(menuData).filter(isPublicApprovedDrink);

      setCategories(getMenuCategories(menuData));
      
      // فلترة المشروبات المُوافق عليها والنشطة فقط
      const approvedDrinks = (aiDrinksResponse?.data || []).filter(isPublicApprovedDrink);
      setAiDrinks(menuDrinks.length ? menuDrinks : approvedDrinks);
    } catch (err) {
      console.error('خطأ في جلب البيانات:', err);
      setError(err.message || 'حدث خطأ في جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box dir="rtl" sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RestaurantMenuIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            قائمة المنيو
          </Typography>
        </Box>
        
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          centered
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: '0.95rem',
              fontWeight: 'bold',
            }
          }}
        >
          <Tab 
            icon={<LocalCafeIcon />} 
            iconPosition="start"
            label="المنتجات العادية" 
          />
          <Tab 
            icon={<AutoAwesomeIcon />} 
            iconPosition="start"
            label={`المشروبات المخصصة (${aiDrinks.length})`}
          />
        </Tabs>
      </Paper>

      {/* Content */}
      {tabValue === 0 && (
        <Box>
          {categories.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <RestaurantMenuIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                لا توجد منتجات في المنيو
              </Typography>
            </Paper>
          ) : (
            <>
              {categories.map((rawCategory) => {
                const visibleProducts = getVisibleProducts(rawCategory.products);
                const category = { ...rawCategory, products: visibleProducts };

                return (
                <Box key={category.id} sx={{ mb: 3 }}>
                  {/* Category Header */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      mb: 2,
                      p: 1.5,
                      backgroundColor: 'primary.light',
                      borderRadius: 1,
                      color: 'white',
                    }}
                  >
                    {category.image && (
                      <Box
                        component="img"
                        src={resolveImageUrl(category.image)}
                        alt={category.name}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    )}
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {category.description || category.name_en}
                      </Typography>
                    </Box>

                    <Chip
                      label={`${category.products?.length || 0} منتج`}
                      size="small"
                      sx={{ backgroundColor: 'white', color: 'primary.main', fontWeight: 'bold' }}
                    />
                  </Box>

                  {/* Products Grid */}
                  {visibleProducts.length > 0 ? (
                    <Grid container spacing={1.5}>
                      {visibleProducts.map((product) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={product.id}>
                          <ProductCard product={product} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                      <Typography variant="body2" color="text.secondary">
                        لا توجد منتجات في هذه الفئة
                      </Typography>
                    </Paper>
                  )}
                </Box>
                );
              })}
            </>
          )}
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          {aiDrinks.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AutoAwesomeIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                لا توجد مشروبات مخصصة مُعتمدة
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={1.5}>
              {aiDrinks.map((drink) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={drink.id}>
                  <AiDrinkCard drink={drink} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};

// Product Card Component
const ProductCard = ({ product }) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      {product.image ? (
        <CardMedia
          component="img"
          height="160"
          image={resolveImageUrl(product.image)}
          alt={product.name}
          sx={{ objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            height: 160,
            backgroundColor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RestaurantMenuIcon sx={{ fontSize: 50, color: 'text.secondary' }} />
        </Box>
      )}

      <CardContent sx={{ flex: 1, p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
            {product.name}
          </Typography>
          <Chip
            label={product.status === 1 ? 'متاح' : 'غير متاح'}
            size="small"
            color={product.status === 1 ? 'success' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {product.name_en}
        </Typography>

        {product.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.75rem',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {product.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1rem' }}>
              {parseFloat(product.price).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ج.م
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">
              {product.prepare_time} د
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// AI Drink Card Component
const AiDrinkCard = ({ drink }) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        border: '2px solid',
        borderColor: 'info.light',
        backgroundColor: '#f0f8ff',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: 'info.main',
        },
      }}
    >
      {drink.image ? (
        <CardMedia
          component="img"
          height="160"
          image={resolveImageUrl(drink.image)}
          alt={drink.drink_name}
          sx={{ objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            height: 160,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 50, color: 'white' }} />
        </Box>
      )}

      <CardContent sx={{ flex: 1, p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
            {drink.name || drink.drink_name}
          </Typography>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            label="AI"
            size="small"
            color="info"
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
          />
        </Box>

        {drink.name_en && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {drink.name_en}
          </Typography>
        )}

        {drink.temperature && (
          <Chip
            label={drink.temperature}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem', mb: 1 }}
          />
        )}

        {drink.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.75rem',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {drink.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1rem' }}>
              {parseFloat(drink.price).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ج.م
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="info.main" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
              {drink.materials?.length || 0} مكون
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MenuPage;
